import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { ImportResult } from '~/domain/services/dicomProcessor';
import { reformatBlockedReason } from '~/domain/services/imageService';
import type { ReformatTarget } from '~/domain/services/imageService';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { enqueueSnackbar } from '~/utils/notifications';
import { REFORMAT_BLOCKED_MESSAGE_IDS } from './DatasetList/reformatBlockedMessages';
import { reformatTargetFor } from './DatasetList/useReformatBlockedReason';
import ReformatDialog from './ReformatDialog';

export function useReformatImage(): {
    openReformat: (image: AvailableImage) => void;
    reformatDialog: React.ReactNode;
} {
    const intl = useIntl();
    const { initializeEnvironment } = useViewportEnvironment();
    const [target, setTarget] = useState<ReformatTarget | null>(null);

    const openReformat = useCallback(
        (image: AvailableImage) => {
            const candidate = reformatTargetFor(image);
            if (!candidate) {
                return;
            }
            // Guarded here too: some entry points bypass the disabled button and its tooltip.
            const blocked = reformatBlockedReason(candidate);
            if (blocked !== null) {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: REFORMAT_BLOCKED_MESSAGE_IDS[blocked],
                    }),
                    { variant: 'warning' }
                );
                return;
            }
            setTarget(candidate);
        },
        [intl]
    );

    const handleComplete = useCallback(
        (importResult: ImportResult) => {
            initializeEnvironment(importResult);
            setTarget(null);
        },
        [initializeEnvironment]
    );

    return {
        openReformat,
        reformatDialog: target && (
            <ReformatDialog
                open
                onClose={() => setTarget(null)}
                target={target}
                onReformatComplete={handleComplete}
            />
        ),
    };
}
